import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resumeService } from '../services/resumeService';
import { DetailedResume } from '../types/resume';
import { JobForYou } from '../types/jobs';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, updateUserProfile } = useAuth();
  const [isEditingLinkedIn, setIsEditingLinkedIn] = useState(false);
  const [linkedInUrl, setLinkedInUrl] = useState(userProfile?.linkedInURL || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Resume-related state
  const [resumes, setResumes] = useState<DetailedResume[]>([]);
  const [selectedResume, setSelectedResume] = useState<DetailedResume | null>(null);
  const [isUploadingResume, setIsUploadingResume] = useState(false);
  const [resumeError, setResumeError] = useState('');
  const [loadingResumes, setLoadingResumes] = useState(true);
  
  // Per-bubble editing: which resume and which bubble key (e.g. 'contact', 'education-0', 'experience-1', 'queries')
  const [editingBubble, setEditingBubble] = useState<{ resumeId: string; key: string } | null>(null);
  const [editedResumeData, setEditedResumeData] = useState<DetailedResume | null>(null);
  // Raw multi-line text used only while editing the Qualified Job Titles bubble
  const [queriesDraft, setQueriesDraft] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  // Job match counts for the Matches box
  const [matchCounts, setMatchCounts] = useState<{ high: number; medium: number; low: number; total: number } | null>(null);

  const loadResumes = useCallback(async (): Promise<DetailedResume[]> => {
    try {
      setLoadingResumes(true);
      setResumeError('');
      const resumeList = await resumeService.getResumes();
      console.log('📋 Loaded resumes:', resumeList);
      setResumes(resumeList || []);
      return resumeList || [];
    } catch (error: any) {
      console.error('Error loading resumes:', error);
      if (!error.message?.includes('Not authenticated')) {
        setResumeError('Failed to load resumes: ' + error.message);
      }
      return [];
    } finally {
      setLoadingResumes(false);
    }
  }, []);

  // Load resumes when user profile is available
  useEffect(() => {
    if (userProfile) {
      console.log('👤 User profile loaded, fetching resumes...');
      loadResumes();
    }
  }, [userProfile, loadResumes]);

  // Load job match counts for the primary resume
  useEffect(() => {
    if (resumes.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const jobs: JobForYou[] = await resumeService.getJobsForResume(resumes[0].id);
        if (cancelled) return;
        const counts = { high: 0, medium: 0, low: 0, total: 0 };
        for (const job of jobs) {
          if (job.jobFit === 'high') { counts.high++; counts.total++; }
          else if (job.jobFit === 'medium') { counts.medium++; counts.total++; }
          else if (job.jobFit === 'low') { counts.low++; counts.total++; }
        }
        setMatchCounts(counts);
      } catch {
        // silently ignore — box simply won't show
      }
    })();
    return () => { cancelled = true; };
  }, [resumes]);

  const validateLinkedInUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is allowed
    const linkedinRegex = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
    return linkedinRegex.test(url);
  };

  const handleLinkedInSave = async () => {
    setError('');
    
    if (!validateLinkedInUrl(linkedInUrl)) {
      setError('Please enter a valid LinkedIn URL (e.g., https://www.linkedin.com/in/yourname)');
      return;
    }
    
    setIsLoading(true);
    try {
      await updateUserProfile({ linkedInURL: linkedInUrl.trim() || undefined });
      setIsEditingLinkedIn(false);
    } catch (error) {
      console.error('Error updating LinkedIn URL:', error);
      setError('Failed to update LinkedIn URL. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInCancel = () => {
    setLinkedInUrl(userProfile?.linkedInURL || '');
    setIsEditingLinkedIn(false);
    setError('');
  };

  const handleResumeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
      setResumeError('Please upload a PDF, DOCX, or TXT file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setResumeError('File size must be less than 10MB');
      return;
    }

    // Check if filename already exists (only if we have resumes loaded)
    if (resumes.length > 0) {
      const existingResume = resumes.find(resume => resume.sourceFilename === file.name);
      if (existingResume) {
        setResumeError(`A resume with the filename "${file.name}" already exists. Please rename your file or choose a different file.`);
        return;
      }
    }

    setIsUploadingResume(true);
    setResumeError('');

    try {
      const response = await resumeService.uploadResume(file);
      console.log('Resume uploaded successfully:', response);
      
      // Reload resumes to show the new one
      await loadResumes();
      
      // Clear the file input
      event.target.value = '';
      
    } catch (error: any) {
      console.error('Resume upload error:', error);
      
      // Handle duplicate filename error from backend
      if (error.message.includes('already exists')) {
        setResumeError(`A resume with this filename already exists. Please rename your file and try again.`);
      } else {
        setResumeError(error.message || 'Failed to upload resume. Please try again.');
      }
    } finally {
      setIsUploadingResume(false);
    }
  };

  const handleDeleteResume = async (resumeId: string, filename: string) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      await resumeService.deleteResume(resumeId);
      console.log('Resume deleted successfully');
      
      // Reload resumes to remove the deleted one
      await loadResumes();
      
      // Close the selected resume if it was the one deleted
      if (selectedResume?.id === resumeId) {
        setSelectedResume(null);
      }
    } catch (error: any) {
      console.error('Resume delete error:', error);
      setResumeError(error.message || 'Failed to delete resume. Please try again.');
    }
  };

  const isBubbleEditing = (resumeId: string, key: string) =>
    editingBubble?.resumeId === resumeId && editingBubble?.key === key;

  const startEditingBubble = (resume: DetailedResume, key: string) => {
    setEditingBubble({ resumeId: resume.id, key });
    setEditedResumeData({ ...resume });
    if (key === 'queries') {
      setQueriesDraft((resume.queries ?? []).join('\n'));
    }
    setResumeError('');
  };

  const exitBubbleWithoutSaving = () => {
    setEditingBubble(null);
    setEditedResumeData(null);
    setQueriesDraft('');
    setResumeError('');
  };

  const saveBubbleAndExit = async () => {
    if (!editingBubble || !editedResumeData) return;

    const { resumeId, key } = editingBubble;
    setIsSaving(true);
    setResumeError('');

    try {
      let partial: Partial<DetailedResume> = {};
      if (key === 'contact') {
        partial = { contact: editedResumeData.contact };
      } else if (key === 'professional_summary') {
        partial = { professional_summary: editedResumeData.professional_summary };
      } else if (key === 'skills') {
        partial = { skills: editedResumeData.skills ?? [] };
      } else if (key === 'interests') {
        partial = { interests: editedResumeData.interests ?? [] };
      } else if (key === 'certifications') {
        partial = { certifications: editedResumeData.certifications ?? [] };
      } else if (key.startsWith('education')) {
        partial = {
          education: (editedResumeData.education ?? []).map((edu) => ({
            ...edu,
            startDate: normalizeDateForSave(edu.startDate, false),
            endDate: normalizeDateForSave(edu.endDate, true),
          })),
        };
      } else if (key.startsWith('experience')) {
        partial = {
          experience: (editedResumeData.experience ?? []).map((exp) => ({
            ...exp,
            startDate: normalizeDateForSave(exp.startDate, false),
            endDate: normalizeDateForSave(exp.endDate, true),
          })),
        };
      } else if (key === 'queries') {
        const rawLines = queriesDraft.split('\n');
        const trimmed = rawLines.map((line) => line.trim());
        const nonEmpty = trimmed.filter((line) => line.length > 0);

        // If there is at least one non-empty title and any blank line, block save
        // so the user must remove empty rows between titles.
        if (nonEmpty.length > 0 && trimmed.some((line) => line === '')) {
          setResumeError('Please remove empty lines between job titles before saving.');
          setIsSaving(false);
          return;
        }

        const existingResume = resumes.find((r) => r.id === resumeId);
        const previousQueries = (existingResume?.queries ?? []).map((q) => q.trim()).filter(Boolean);
        const previousSet = new Set(previousQueries);
        const nextSet = new Set(nonEmpty);
        const removed = previousQueries.filter((q) => !nextSet.has(q));
        const added = nonEmpty.filter((q) => !previousSet.has(q));
        if (removed.length > 0) {
          const previewRemoved = removed.slice(0, 5).join('\n- ');
          const previewAdded = added.slice(0, 5).join('\n+ ');
          const message =
            `You are removing ${removed.length} query title(s)` +
            (added.length ? ` and adding ${added.length}.` : '.') +
            `\n\nRemoved:\n- ${previewRemoved}` +
            (removed.length > 5 ? '\n- ...' : '') +
            (added.length
              ? `\n\nAdded:\n+ ${previewAdded}${added.length > 5 ? '\n+ ...' : ''}`
              : '') +
            '\n\nContinue and replace saved query list?';
          if (!window.confirm(message)) {
            setIsSaving(false);
            return;
          }
        }

        partial = { queries: nonEmpty };
      }

      await resumeService.updateResume(resumeId, partial);
      const updatedList = await loadResumes();
      if (selectedResume?.id === resumeId) {
        const updated = updatedList.find(r => r.id === resumeId);
        if (updated) setSelectedResume(updated);
      }
      setEditingBubble(null);
      setEditedResumeData(null);
      setQueriesDraft('');
    } catch (error: any) {
      console.error('Resume update error:', error);
      setResumeError(error.message || 'Failed to update. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResumeSelection = (resume: DetailedResume) => {
    if (editingBubble) {
      exitBubbleWithoutSaving();
    }
    setSelectedResume(selectedResume === resume ? null : resume);
  };

  const updateEditedField = (field: string, value: any) => {
    if (!editedResumeData) return;
    
    setEditedResumeData({
      ...editedResumeData,
      [field]: value
    });
  };

  const updateEducationField = (index: number, field: string, value: any) => {
    if (!editedResumeData) return;
    
    const updatedEducation = [...editedResumeData.education];
    updatedEducation[index] = {
      ...updatedEducation[index],
      [field]: value
    };

    setEditedResumeData({
      ...editedResumeData,
      education: updatedEducation
    });
  };

  const updateExperienceField = (index: number, field: string, value: any) => {
    if (!editedResumeData) return;
    
    const updatedExperience = [...editedResumeData.experience];
    updatedExperience[index] = {
      ...updatedExperience[index],
      [field]: value
    };

    setEditedResumeData({
      ...editedResumeData,
      experience: updatedExperience
    });
  };

  // Display only (no time): "Jan 2020" or "Present"
  const formatDate = (dateString: string | null) => {
    const s = dateString?.trim();
    if (!s || s.toLowerCase() === 'present') return 'Present';
    try {
      let toParse = s;
      if (s.includes('T')) toParse = s.substring(0, 10);
      else if (/^\d{4}-\d{2}$/.test(s)) toParse = s + '-01';
      const date = new Date(toParse);
      if (isNaN(date.getTime())) return s;
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch {
      return s || 'Present';
    }
  };

  // Value for <input type="month">: "YYYY-MM" or "" (empty for Present/invalid)
  const dateToMonthValue = (dateString: string | null): string => {
    const s = dateString?.trim();
    if (!s || s.toLowerCase() === 'present') return '';
    if (/^\d{4}-\d{2}$/.test(s)) return s;
    if (s.includes('T')) return s.substring(0, 7);
    try {
      const date = new Date(s.length === 10 ? s : s + '-01');
      if (isNaN(date.getTime())) return '';
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      return `${y}-${String(m).padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  // Normalize for save: only "YYYY-MM" or "Present" (end date only)
  const normalizeDateForSave = (value: string | null, isEndDate: boolean): string | null => {
    const s = value?.trim() || null;
    if (!s) return null;
    if (isEndDate && s.toLowerCase() === 'present') return 'Present';
    if (/^\d{4}-\d{2}$/.test(s)) return s;
    const monthVal = dateToMonthValue(s);
    return monthVal || null;
  };


  return (
    <div className="min-h-screen bg-ts-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="space-y-4">
          {/* Welcome Section */}
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Welcome, {userProfile?.firstName}!
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              Your account has been successfully created
            </p>
            
            {/* Quick Actions */}
            {resumes.length > 0 && (
              <div className="flex justify-center gap-4 mb-8">
                <button
                  onClick={() => navigate('/connections')}
                  className="px-6 py-3 bg-ts-navy text-white font-medium rounded-lg hover:bg-ts-charcoal transition-colors shadow-lg hover:shadow-xl"
                >
                  📊 Analyze Connections
                </button>
              </div>
            )}
          </div>
          
          {/* User Profile Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto border border-ts-border">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Profile Information</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                    {userProfile?.firstName || 'Not provided'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                    {userProfile?.lastName || 'Not provided'}
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <p className="text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                  {userProfile?.email}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  LinkedIn Profile
                </label>
                {isEditingLinkedIn ? (
                  <div className="space-y-2">
                    <input
                      type="url"
                      value={linkedInUrl}
                      onChange={(e) => setLinkedInUrl(e.target.value)}
                      placeholder="https://www.linkedin.com/in/yourname"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold"
                    />
                    {error && (
                      <p className="text-sm text-red-600">{error}</p>
                    )}
                    <div className="flex space-x-2">
                      <button
                        onClick={handleLinkedInSave}
                        disabled={isLoading}
                        className="px-3 py-1 bg-ts-navy text-white text-sm rounded hover:bg-ts-charcoal disabled:opacity-50"
                      >
                        {isLoading ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleLinkedInCancel}
                        className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="text-gray-900">
                      {userProfile?.linkedInURL ? (
                        <a 
                          href={userProfile.linkedInURL} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-ts-gold hover:text-ts-gold-deep underline"
                        >
                          {userProfile.linkedInURL}
                        </a>
                      ) : (
                        <span className="text-gray-500">Not provided</span>
                      )}
                    </span>
                    <button
                      onClick={() => setIsEditingLinkedIn(true)}
                      className="ml-2 text-ts-gold hover:text-ts-gold-deep text-sm"
                    >
                      {userProfile?.linkedInURL ? 'Edit' : 'Add'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Resume Management Section */}
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto border border-ts-border">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">Resume Management</h2>
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleResumeUpload}
                  disabled={isUploadingResume}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  id="resume-upload"
                />
                <label
                  htmlFor="resume-upload"
                  className={`inline-flex items-center px-4 py-2 border border-ts-border text-sm font-medium rounded-md text-white ${
                    isUploadingResume 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-ts-navy hover:bg-ts-charcoal cursor-pointer'
                  } transition-colors duration-200`}
                >
                  {isUploadingResume ? 'Uploading...' : 'Upload Resume'}
                </label>
              </div>
            </div>

            {isUploadingResume && (
              <div className="mb-4 flex items-center justify-center py-4">
                <div className="flex items-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ts-gold"></div>
                  <span className="text-sm text-gray-600">Uploading resume...</span>
                </div>
              </div>
            )}

            {resumeError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{resumeError}</p>
              </div>
            )}

            {loadingResumes ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ts-gold mx-auto mb-2"></div>
                <p className="text-gray-600">Loading resumes...</p>
              </div>
            ) : (!loadingResumes && resumes.length === 0) ? (
              <div className="text-center py-8">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No resumes uploaded</h3>
              </div>
            ) : (
              <div className="space-y-4">
                {resumes.map((resume) => (
                  <div
                    key={resume.id}
                    className="border rounded-lg p-4 cursor-pointer transition-colors duration-200 hover:border-gray-300"
                    onClick={() => handleResumeSelection(resume)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">
                          {resume.sourceFilename || 'Unnamed Resume'}
                        </h3>
                        <div className="mt-1 text-sm text-gray-600">
                          <p>📧 {resume.contact?.email || 'No email'}</p>
                          <p>📍 {resume.contact?.location || 'No location'}</p>
                        </div>
                      </div>
                <div className="flex items-center space-x-2">
                  <div className="text-right text-sm text-gray-500">
                    <p>Uploaded</p>
                    <p>{new Date(resume.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent opening the resume when clicking delete
                      handleDeleteResume(resume.id, resume.sourceFilename || 'Unnamed Resume');
                    }}
                    className="text-red-500 hover:text-red-700 p-1 rounded"
                    title="Delete resume"
                  >
                    🗑️
                  </button>
                </div>
                    </div>

                    {selectedResume === resume && (
                      <div className="mt-4 pt-4 border-t space-y-4" onClick={(e) => e.stopPropagation()}>
                        {/* Contact Information bubble */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                          <h5 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h5>
                          {!isBubbleEditing(resume.id, 'contact') ? (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => startEditingBubble(resume, 'contact')}
                              onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, 'contact')}
                              className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors"
                            >
                              <p className="text-sm text-gray-700"><span className="font-medium text-gray-900">Email:</span> {resume.contact?.email || 'Not provided'}</p>
                              <p className="text-sm text-gray-700 mt-1"><span className="font-medium text-gray-900">Phone:</span> {resume.contact?.phone || 'Not provided'}</p>
                              <p className="text-sm text-gray-700 mt-1"><span className="font-medium text-gray-900">Location:</span> {resume.contact?.location || 'Not provided'}</p>
                              <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                                  <input
                                    type="email"
                                    value={editedResumeData?.contact?.email || ''}
                                    onChange={(e) => updateEditedField('contact', {
                                      ...editedResumeData?.contact,
                                      email: e.target.value
                                    })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400"
                                    placeholder="your.email@example.com"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                                  <input
                                    type="tel"
                                    value={editedResumeData?.contact?.phone || ''}
                                    onChange={(e) => updateEditedField('contact', {
                                      ...editedResumeData?.contact,
                                      phone: e.target.value
                                    })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400"
                                    placeholder="+1 (555) 123-4567"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                                  <input
                                    type="text"
                                    value={editedResumeData?.contact?.location || ''}
                                    onChange={(e) => updateEditedField('contact', {
                                      ...editedResumeData?.contact,
                                      location: e.target.value
                                    })}
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400"
                                    placeholder="City, State or City, Country"
                                  />
                                </div>
                              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                                <button
                                  type="button"
                                  onClick={saveBubbleAndExit}
                                  disabled={isSaving}
                                  className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md"
                                >
                                  {isSaving ? 'Saving...' : 'Save and exit'}
                                </button>
                                <button
                                  type="button"
                                  onClick={exitBubbleWithoutSaving}
                                  className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow"
                                >
                                  Exit
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Professional Summary bubble */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                          <h5 className="text-lg font-semibold text-gray-900 mb-3">Professional Summary</h5>
                          {!isBubbleEditing(resume.id, 'professional_summary') ? (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => startEditingBubble(resume, 'professional_summary')}
                              onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, 'professional_summary')}
                              className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors"
                            >
                              {resume.professional_summary ? (
                                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{resume.professional_summary}</p>
                              ) : (
                                <p className="text-gray-500 text-sm">Not provided. Click to add.</p>
                              )}
                              <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                            </div>
                          ) : (
                            <>
                              <textarea
                                value={editedResumeData?.professional_summary || ''}
                                onChange={(e) => updateEditedField('professional_summary', e.target.value)}
                                rows={5}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400 resize-y"
                                placeholder="Write a brief summary of your professional background, key skills, and career objectives..."
                              />
                              <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                                <button type="button" onClick={saveBubbleAndExit} disabled={isSaving} className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md">{isSaving ? 'Saving...' : 'Save and exit'}</button>
                                <button type="button" onClick={exitBubbleWithoutSaving} className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow">Exit</button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Skills bubble */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                          <h5 className="text-lg font-semibold text-gray-900 mb-3">Skills</h5>
                          {!isBubbleEditing(resume.id, 'skills') ? (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => startEditingBubble(resume, 'skills')}
                              onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, 'skills')}
                              className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors"
                            >
                              {resume.skills && resume.skills.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {resume.skills.map((s, i) => <span key={i} className="inline-block bg-ts-gold/10 text-ts-gold-deep text-sm px-3 py-1.5 rounded-full font-medium">{s}</span>)}
                                </div>
                              ) : <p className="text-gray-500 text-sm">None. Click to add.</p>}
                              <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                            </div>
                          ) : (
                            <>
                              <textarea
                                value={editedResumeData?.skills?.join(', ') || ''}
                                onChange={(e) => updateEditedField('skills', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                rows={3}
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400 resize-y"
                                placeholder="e.g. JavaScript, Python, React, Project Management..."
                              />
                              <p className="text-xs text-gray-500 mt-1.5">Separate skills with commas</p>
                              <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                                <button type="button" onClick={saveBubbleAndExit} disabled={isSaving} className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md">{isSaving ? 'Saving...' : 'Save and exit'}</button>
                                <button type="button" onClick={exitBubbleWithoutSaving} className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow">Exit</button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Interests bubble */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                          <h5 className="text-lg font-semibold text-gray-900 mb-3">Interests</h5>
                          {!isBubbleEditing(resume.id, 'interests') ? (
                            <div role="button" tabIndex={0} onClick={() => startEditingBubble(resume, 'interests')} onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, 'interests')} className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors">
                              {resume.interests?.length ? <div className="flex flex-wrap gap-2">{resume.interests.map((s, i) => <span key={i} className="inline-block bg-green-100 text-green-800 text-sm px-3 py-1.5 rounded-full font-medium">{s}</span>)}</div> : <p className="text-gray-500 text-sm">None. Click to add.</p>}
                              <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                            </div>
                          ) : (
                            <>
                              <textarea value={editedResumeData?.interests?.join(', ') || ''} onChange={(e) => updateEditedField('interests', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} rows={2} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400 resize-y" placeholder="e.g. Photography, Hiking, Reading..." />
                              <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                                <button type="button" onClick={saveBubbleAndExit} disabled={isSaving} className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md">{isSaving ? 'Saving...' : 'Save and exit'}</button>
                                <button type="button" onClick={exitBubbleWithoutSaving} className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow">Exit</button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Certifications bubble */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                          <h5 className="text-lg font-semibold text-gray-900 mb-3">Certifications</h5>
                          {!isBubbleEditing(resume.id, 'certifications') ? (
                            <div role="button" tabIndex={0} onClick={() => startEditingBubble(resume, 'certifications')} onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, 'certifications')} className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors">
                              {resume.certifications?.length ? <div className="flex flex-wrap gap-2">{resume.certifications.map((s, i) => <span key={i} className="inline-block bg-purple-100 text-purple-800 text-sm px-3 py-1.5 rounded-full font-medium">{s}</span>)}</div> : <p className="text-gray-500 text-sm">None. Click to add.</p>}
                              <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                            </div>
                          ) : (
                            <>
                              <textarea value={editedResumeData?.certifications?.join(', ') || ''} onChange={(e) => updateEditedField('certifications', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} rows={2} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400 resize-y" placeholder="e.g. AWS Certified Solutions Architect, PMP..." />
                              <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                                <button type="button" onClick={saveBubbleAndExit} disabled={isSaving} className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md">{isSaving ? 'Saving...' : 'Save and exit'}</button>
                                <button type="button" onClick={exitBubbleWithoutSaving} className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow">Exit</button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Education - one bubble per entry */}
                        {resume.education && resume.education.length > 0 && (
                          <div>
                            <h5 className="text-xl font-semibold text-gray-900 mb-4">Education</h5>
                            <div className="space-y-4">
                              {resume.education.map((edu, eduIndex) => {
                                const key = `education-${eduIndex}`;
                                return (
                                  <div key={eduIndex} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                    {!isBubbleEditing(resume.id, key) ? (
                                      <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => startEditingBubble(resume, key)}
                                        onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, key)}
                                        className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors"
                                      >
                                        <p className="text-lg font-semibold text-gray-900">{edu.schoolName}</p>
                                        <p className="text-base text-gray-700 mt-1">{edu.degree}</p>
                                        {edu.concentration && <p className="text-sm text-gray-600 mt-1">{edu.concentration}</p>}
                                        <p className="text-sm text-gray-500 mt-2">{formatDate(edu.startDate)} - {formatDate(edu.endDate)}</p>
                                        <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                                      </div>
                                    ) : (
                                      <div className="space-y-4">
                                        {editedResumeData?.education?.[eduIndex] && (
                                          <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">School Name</label>
                                                <input type="text" value={editedResumeData.education[eduIndex].schoolName || ''} onChange={(e) => updateEducationField(eduIndex, 'schoolName', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400" placeholder="e.g. Stanford University" />
                                              </div>
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Degree</label>
                                                <input type="text" value={editedResumeData.education[eduIndex].degree || ''} onChange={(e) => updateEducationField(eduIndex, 'degree', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400" placeholder="e.g. BS, MS, PhD" />
                                              </div>
                                            </div>
                                            <div>
                                              <label className="block text-sm font-semibold text-gray-700 mb-2">Concentration/Major</label>
                                              <input type="text" value={editedResumeData.education[eduIndex].concentration || ''} onChange={(e) => updateEducationField(eduIndex, 'concentration', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400" placeholder="Optional - e.g. Computer Science" />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                                                <input type="month" value={dateToMonthValue(editedResumeData.education[eduIndex].startDate)} onChange={(e) => updateEducationField(eduIndex, 'startDate', e.target.value || null)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900" title="Format: YYYY-MM" />
                                                <p className="text-xs text-gray-500 mt-1.5">Select month and year</p>
                                              </div>
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                                                <input type="month" value={dateToMonthValue(editedResumeData.education[eduIndex].endDate)} onChange={(e) => updateEducationField(eduIndex, 'endDate', e.target.value || null)} disabled={(editedResumeData.education[eduIndex].endDate || '').toLowerCase() === 'present'} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed" title="Format: YYYY-MM" />
                                                <p className="text-xs text-gray-500 mt-1.5">Select month and year</p>
                                              </div>
                                            </div>
                                            <div className="mt-2">
                                              <label className="flex items-center gap-2 cursor-pointer group">
                                                <input type="checkbox" checked={(editedResumeData.education[eduIndex].endDate || '').toLowerCase() === 'present'} onChange={(e) => updateEducationField(eduIndex, 'endDate', e.target.checked ? 'Present' : null)} className="w-4 h-4 rounded border-gray-300 text-ts-gold focus:ring-2 focus:ring-ts-gold cursor-pointer" />
                                                <span className="text-sm text-gray-700 group-hover:text-gray-900">Currently enrolled (Present)</span>
                                              </label>
                                            </div>
                                            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                                              <button type="button" onClick={saveBubbleAndExit} disabled={isSaving} className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md">{isSaving ? 'Saving...' : 'Save and exit'}</button>
                                              <button type="button" onClick={exitBubbleWithoutSaving} className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow">Exit</button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Experience - one bubble per entry */}
                        {resume.experience && resume.experience.length > 0 && (
                          <div>
                            <h5 className="text-xl font-semibold text-gray-900 mb-4">Experience</h5>
                            <div className="space-y-4">
                              {resume.experience.map((exp, expIndex) => {
                                const key = `experience-${expIndex}`;
                                return (
                                  <div key={expIndex} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                    {!isBubbleEditing(resume.id, key) ? (
                                      <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => startEditingBubble(resume, key)}
                                        onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, key)}
                                        className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors"
                                      >
                                        <p className="text-lg font-semibold text-gray-900">{exp.title}</p>
                                        <p className="text-base text-gray-700 mt-1">{exp.company}</p>
                                        <p className="text-sm text-gray-500 mt-2">{formatDate(exp.startDate)} - {formatDate(exp.endDate)}</p>
                                        {exp.roleDescription && <p className="mt-3 text-sm text-gray-600 leading-relaxed whitespace-pre-line">{exp.roleDescription}</p>}
                                        <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                                      </div>
                                    ) : (
                                      <div className="space-y-4">
                                        {editedResumeData?.experience?.[expIndex] && (
                                          <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Job Title</label>
                                                <input type="text" value={editedResumeData.experience[expIndex].title || ''} onChange={(e) => updateExperienceField(expIndex, 'title', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400" placeholder="e.g. Software Engineer" />
                                              </div>
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                                                <input type="text" value={editedResumeData.experience[expIndex].company || ''} onChange={(e) => updateExperienceField(expIndex, 'company', e.target.value)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400" placeholder="e.g. Acme Inc." />
                                              </div>
                                            </div>
                                            <div>
                                              <label className="block text-sm font-semibold text-gray-700 mb-2">Role Description</label>
                                              <textarea value={editedResumeData.experience[expIndex].roleDescription || ''} onChange={(e) => updateExperienceField(expIndex, 'roleDescription', e.target.value)} rows={4} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400 resize-y" placeholder="Describe your responsibilities, achievements, and impact..." />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                                                <input type="month" value={dateToMonthValue(editedResumeData.experience[expIndex].startDate)} onChange={(e) => updateExperienceField(expIndex, 'startDate', e.target.value || null)} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900" title="Format: YYYY-MM" />
                                                <p className="text-xs text-gray-500 mt-1.5">Select month and year</p>
                                              </div>
                                              <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                                                <input type="month" value={dateToMonthValue(editedResumeData.experience[expIndex].endDate)} onChange={(e) => updateExperienceField(expIndex, 'endDate', e.target.value || null)} disabled={(editedResumeData.experience[expIndex].endDate || '').toLowerCase() === 'present'} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed" title="Format: YYYY-MM" />
                                                <p className="text-xs text-gray-500 mt-1.5">Select month and year</p>
                                              </div>
                                            </div>
                                            <div className="mt-2">
                                              <label className="flex items-center gap-2 cursor-pointer group">
                                                <input type="checkbox" checked={(editedResumeData.experience[expIndex].endDate || '').toLowerCase() === 'present'} onChange={(e) => updateExperienceField(expIndex, 'endDate', e.target.checked ? 'Present' : null)} className="w-4 h-4 rounded border-gray-300 text-ts-gold focus:ring-2 focus:ring-ts-gold cursor-pointer" />
                                                <span className="text-sm text-gray-700 group-hover:text-gray-900">Current position (Present)</span>
                                              </label>
                                            </div>
                                            <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                                              <button type="button" onClick={saveBubbleAndExit} disabled={isSaving} className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md">{isSaving ? 'Saving...' : 'Save and exit'}</button>
                                              <button type="button" onClick={exitBubbleWithoutSaving} className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow">Exit</button>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Qualified Job Titles - editable */}
                        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
                          <h5 className="text-lg font-semibold text-gray-900 mb-3">Qualified Job Titles</h5>
                          {!isBubbleEditing(resume.id, 'queries') ? (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => startEditingBubble(resume, 'queries')}
                              onKeyDown={(e) => e.key === 'Enter' && startEditingBubble(resume, 'queries')}
                              className="cursor-pointer hover:bg-gray-50 rounded-lg p-3 -m-3 transition-colors"
                            >
                              {(resume.queries?.length ?? 0) > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {resume.queries!.map((title, i) => <span key={i} className="inline-block bg-orange-100 text-orange-800 text-sm px-3 py-1.5 rounded-full font-medium">{title}</span>)}
                                </div>
                              ) : (
                                <p className="text-gray-500 text-sm">None. Click to add qualified job titles.</p>
                              )}
                              <p className="text-xs text-ts-gold mt-3 font-medium">Click to edit</p>
                            </div>
                          ) : (
                              <div className="space-y-3">
                                <p className="text-xs text-gray-600">Enter one job title per line.</p>
                                <textarea
                                  value={queriesDraft}
                                  onChange={(e) => setQueriesDraft(e.target.value)}
                                  rows={6}
                                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ts-gold focus:border-ts-gold transition-all bg-white text-gray-900 placeholder-gray-400 resize-y"
                                  placeholder="Software Engineer&#10;Product Manager&#10;Data Scientist..."
                                />
                                <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                                  <button type="button" onClick={saveBubbleAndExit} disabled={isSaving} className="px-5 py-2.5 bg-ts-navy text-white text-sm font-medium rounded-lg hover:bg-ts-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md">{isSaving ? 'Saving...' : 'Save and exit'}</button>
                                  <button type="button" onClick={exitBubbleWithoutSaving} className="px-5 py-2.5 bg-white text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm hover:shadow">Exit</button>
                                </div>
                              </div>
                            )}
                          </div>

                        {resumeError && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-600">{resumeError}</p>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Matches box — shown when job fit scores exist for primary resume */}
          {matchCounts && matchCounts.total > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto border border-ts-border">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900">Matches</h2>
                <button
                  onClick={() => navigate('/jobs-for-you')}
                  className="px-4 py-2 bg-ts-navy text-white font-medium rounded-lg hover:bg-ts-charcoal transition-colors text-sm"
                >
                  View matches →
                </button>
              </div>
              <div className="flex gap-4 justify-center">
                {matchCounts.high > 0 && (
                  <div className="flex-1 bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-green-700">{matchCounts.high}</div>
                    <div className="text-sm font-medium text-green-600 mt-1">High fit</div>
                  </div>
                )}
                {matchCounts.medium > 0 && (
                  <div className="flex-1 bg-yellow-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-yellow-700">{matchCounts.medium}</div>
                    <div className="text-sm font-medium text-yellow-600 mt-1">Medium fit</div>
                  </div>
                )}
                {matchCounts.low > 0 && (
                  <div className="flex-1 bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-red-700">{matchCounts.low}</div>
                    <div className="text-sm font-medium text-red-600 mt-1">Low fit</div>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-4 text-center">
                {matchCounts.total} job {matchCounts.total === 1 ? 'match' : 'matches'} scored for your resume
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;